import { Suspense, lazy, type ReactNode } from 'react';
import { createRoute, useNavigate, type AnyRoute } from '@tanstack/react-router';

import { LoadingState } from '../components/LoadingState';
import { AdminSessionProvider } from './AdminSessionContext';
import { HqShell } from './hq/HqShell';
import { parseClientesSearch, parseFinanzasSearch } from './lib/search-params';
import { RequireAdmin, RequireAdminMustChangePassword } from './RequireAdmin';
import type { ClientesSearch } from './routes/Clientes';
import type { FinanzasSearch } from './routes/Finanzas';

/**
 * Mounts the admin session world once per admin page. Lives here (not
 * App.tsx) so the owner shell never has to know the admin session exists;
 * `useNavigate` needs the router context, which every admin route already
 * renders inside.
 */
function AdminSessionShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <AdminSessionProvider onUnauthorized={() => void navigate({ to: '/admin/entrar', replace: true })}>
      {children}
    </AdminSessionProvider>
  );
}

/**
 * The whole /admin tree, built as a lazy route group: every page component
 * is a dynamic import, so the admin bundle code-splits away from the owner
 * app's main chunk instead of shipping on every visit to /entrar or /.
 * `router.tsx` only wires this in (`createAdminRoutes(rootRoute)` +
 * `addChildren`); nothing about the owner routes changes.
 */
const AdminEntrarPage = lazy(() => import('./routes/Entrar').then((m) => ({ default: m.AdminEntrarPage })));
const AdminCambiarContrasenaPage = lazy(() =>
  import('./routes/CambiarContrasena').then((m) => ({ default: m.AdminCambiarContrasenaPage })),
);
const InicioPage = lazy(() => import('./routes/Inicio').then((m) => ({ default: m.InicioPage })));
const ClientesPage = lazy(() => import('./routes/Clientes').then((m) => ({ default: m.ClientesPage })));
const NegocioDetailPage = lazy(() =>
  import('./routes/NegocioDetail').then((m) => ({ default: m.NegocioDetailPage })),
);
const OrganizacionesPage = lazy(() =>
  import('./routes/Organizaciones').then((m) => ({ default: m.OrganizacionesPage })),
);
const OrganizacionDetailPage = lazy(() =>
  import('./routes/OrganizacionDetail').then((m) => ({ default: m.OrganizacionDetailPage })),
);
const PagosPage = lazy(() => import('./routes/Pagos').then((m) => ({ default: m.PagosPage })));
const AdminFinanzasPage = lazy(() =>
  import('./routes/Finanzas').then((m) => ({ default: m.AdminFinanzasPage })),
);
const DaleAiPage = lazy(() => import('./routes/DaleAi').then((m) => ({ default: m.DaleAiPage })));
const EquipoPage = lazy(() => import('./routes/Equipo').then((m) => ({ default: m.EquipoPage })));

function withSuspense(node: ReactNode) {
  return (
    <AdminSessionShell>
      <Suspense fallback={<LoadingState label="Cargando…" />}>{node}</Suspense>
    </AdminSessionShell>
  );
}

/** Every authenticated panel page: guard, then the frame of Dale HQ around it. */
function withShell(node: ReactNode) {
  return withSuspense(
    <RequireAdmin>
      <HqShell>{node}</HqShell>
    </RequireAdmin>,
  );
}

export function createAdminRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const adminEntrarRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/entrar',
    component: () => withSuspense(<AdminEntrarPage />),
  });

  const adminCambiarContrasenaRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/cambiar-contrasena',
    component: () =>
      withSuspense(
        <RequireAdminMustChangePassword>
          <AdminCambiarContrasenaPage />
        </RequireAdminMustChangePassword>,
      ),
  });

  // /admin is Inicio of Dale HQ. It used to be the list of businesses, which is now Clientes.
  const adminInicioRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin',
    component: () => withShell(<InicioPage />),
  });

  const adminClientesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/clientes',
    validateSearch: parseClientesSearch,
    component: () => withShell(<ClientesView />),
  });

  function ClientesView() {
    // Inside this generic builder the router cannot name the type `validateSearch` gives; it is a ClientesSearch.
    const search = adminClientesRoute.useSearch() as ClientesSearch;
    const navigate = useNavigate();
    return (
      <ClientesPage
        search={search}
        onSearchChange={(next) => void navigate({ to: '/admin/clientes', search: next, replace: true })}
      />
    );
  }

  const adminNegocioDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/negocios/$businessId',
    component: () => withShell(<NegocioDetailPage />),
  });

  const adminOrganizacionesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/organizaciones',
    component: () => withShell(<OrganizacionesPage />),
  });

  const adminOrganizacionDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/organizaciones/$orgId',
    component: () => withShell(<OrganizacionDetailPage />),
  });

  // What the old Pagos page held (the ledger and the queue to confirm) stays here and also sits inside Finanzas.
  const adminPagosRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/pagos',
    component: () => withShell(<PagosPage />),
  });

  const adminFinanzasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/finanzas',
    validateSearch: parseFinanzasSearch,
    component: () => withShell(<FinanzasView />),
  });

  function FinanzasView() {
    const search = adminFinanzasRoute.useSearch() as FinanzasSearch;
    const navigate = useNavigate();
    return (
      <AdminFinanzasPage
        search={search}
        onSearchChange={(next) => void navigate({ to: '/admin/finanzas', search: next, replace: true })}
      />
    );
  }

  const adminDaleAiRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/dale-ai',
    component: () => withShell(<DaleAiPage />),
  });

  const adminEquipoRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/equipo',
    component: () => withShell(<EquipoPage />),
  });

  return [
    adminEntrarRoute,
    adminCambiarContrasenaRoute,
    adminInicioRoute,
    adminClientesRoute,
    adminNegocioDetailRoute,
    adminOrganizacionesRoute,
    adminOrganizacionDetailRoute,
    adminPagosRoute,
    adminFinanzasRoute,
    adminDaleAiRoute,
    adminEquipoRoute,
  ] as const;
}
